import { useCallback, useState } from "react";
import { Order } from "../../../types";
import { orderService } from "../../../services/orderService";

/** The server's own message (FastAPI `detail`), else `fallback`. */
export function errorText(err: any, fallback = "Couldn't save the change. Please try again."): string {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((d: any) => d?.msg ?? String(d)).join("; ");
    return fallback;
}

export type Notify = (message: string, severity?: "success" | "error") => void;

/**
 * Saving for the drawer's quick edits: runs the change, then re-fetches the
 * full order and hands it to the screen (drawer, list row, planner card all
 * refresh from it), then shows a toast. A failed change leaves `error` set
 * for the confirmation dialog to show.
 */
export function useQuickEditSave(orderId: string, onOrderChanged: (order: Order) => void, notify: Notify) {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const refresh = useCallback(async (): Promise<Order | null> => {
        try {
            const fresh = await orderService.getOrderById(orderId);
            onOrderChanged(fresh);
            return fresh;
        } catch {
            return null; // the change itself is saved; reopening shows it
        }
    }, [orderId, onOrderChanged]);

    const save = useCallback(
        async (change: () => Promise<unknown>, successMessage: string): Promise<boolean> => {
            setSaving(true);
            setError("");
            try {
                await change();
            } catch (err) {
                setError(errorText(err));
                setSaving(false);
                return false;
            }
            await refresh();
            setSaving(false);
            notify(successMessage);
            return true;
        },
        [refresh, notify],
    );

    return { saving, setSaving, error, setError, save, refresh };
}
